/**
 * Enter or correct the customer's onboarding answers for them.
 *
 * For when the person who started the form has gone, or gives the answers on a
 * call, or replies to the form's email with them typed in. Every question on
 * the customer's form is here, in its order. Only the fields actually changed
 * are sent, and each is stamped on the server with how we got it — so the
 * console never shows our typing as theirs. If the customer later changes an
 * answer on their own form, theirs wins.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { call, isCallFailure } from '../../../lib/callable';
import { EMAIL_MAX, JOB_TITLE_MAX, ORG_NAME_MAX, PERSON_NAME_MAX, PHONE_MAX } from '../../../types/fieldLimits';
import { ORGANIZATION_TYPES } from '../../intake/organizationTypes';
import page from '../../../shared/Page.module.css';
import form from '../../../shared/Form.module.css';
import { Spinner } from '../../../app/Spinner';
import { ENTRY_METHODS } from './entryMethods';
import { StaffMark } from './StaffMark';
import type { StaffField } from './types';
import { GroupCard } from '../../../shared/GroupCard';

type Section = 'organization' | 'champion' | 'signatory' | 'billing' | 'systemAdmin' | 'requirements';
type Answers = Partial<Record<Section | 'submitter', Record<string, unknown>>>;

interface IntakeRead {
  answers: Answers;
  staffFields: Record<string, StaffField>;
  submittedAt: string | null;
  outstanding: string[];
}

const PEOPLE: { key: Section; title: string }[] = [
  { key: 'champion', title: 'Who we will be dealing with (champion)' },
  { key: 'signatory', title: 'Who signs' },
  { key: 'billing', title: 'Where the invoice goes (billing)' },
  { key: 'systemAdmin', title: 'Who will administer it (System Admin)' },
];

const SECTION_WORDS: Record<string, string> = {
  organization: 'their organization', champion: 'the champion', signatory: 'the signatory',
  billing: 'the billing contact', systemAdmin: 'the System Admin',
};

/** Somebody already recorded who could fill a person section: from Contacts, or the named System Admin. */
export interface KnownPerson { name: string; email: string; title?: string | null; phone?: string | null; from: string }

export function IntakeEditor({ orgId, onDone, known = {} }: {
  orgId: string;
  onDone: () => void;
  /** By section key: champion, signatory, billing, systemAdmin. */
  known?: Partial<Record<string, KnownPerson>>;
}) {
  const queryClient = useQueryClient();
  const read = useQuery({
    queryKey: ['org-intake', orgId],
    queryFn: () => call<{ orgId: string }, IntakeRead>('sysAdminGetOrgIntake', { orgId }),
  });

  /** Only what the person changed, so nothing untouched is stamped as ours. */
  const [edits, setEdits] = useState<Answers>({});
  const [method, setMethod] = useState('');
  const [detail, setDetail] = useState('');
  const [givenByName, setGivenByName] = useState('');
  const [givenByEmail, setGivenByEmail] = useState('');
  const [markReturned, setMarkReturned] = useState(false);

  const save = useMutation({
    mutationFn: () => call('sysAdminSaveOrgIntake', {
      orgId,
      answers: edits,
      source: {
        method,
        ...(detail.trim() ? { detail: detail.trim() } : {}),
        ...(givenByName.trim() ? { givenByName: givenByName.trim() } : {}),
        ...(givenByEmail.trim() ? { givenByEmail: givenByEmail.trim() } : {}),
      },
      ...(markReturned ? { markReturned: true } : {}),
    }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['onboarding', orgId] });
      void queryClient.invalidateQueries({ queryKey: ['onboarding-activity', orgId] });
      void queryClient.invalidateQueries({ queryKey: ['org-intake', orgId] });
      onDone();
    },
  });

  if (read.isLoading) return <Spinner label="Loading their answers" />;
  if (read.error || !read.data) {
    return (
      <div className={`${page.notice} ${page.noticeDanger}`} role="alert">
        {isCallFailure(read.error) ? read.error.message : 'Their answers could not be loaded, so nothing can be edited yet.'}
      </div>
    );
  }

  const loaded = read.data.answers ?? {};
  const staff = read.data.staffFields ?? {};
  const value = (section: Section, field: string): unknown =>
    (edits[section] && field in edits[section]!) ? edits[section]![field] : loaded[section]?.[field];
  const set = (section: Section, field: string, v: unknown) =>
    setEdits((e) => ({ ...e, [section]: { ...(e[section] ?? {}), [field]: v } }));
  const text = (section: Section, field: string) => String(value(section, field) ?? '');

  const changedCount = Object.values(edits).reduce((n, s) => n + Object.keys(s ?? {}).length, 0);
  const otherNeedsWords = method === 'other' && !detail.trim();
  const ready = (changedCount > 0 || markReturned) && method !== '' && !otherNeedsWords && !save.isPending;

  // What the customer's own submit would still need, apart from who typed it.
  const merged = (section: Section) => ({ ...(loaded[section] ?? {}), ...(edits[section] ?? {}) });
  const filled = (v: unknown) => typeof v === 'string' && v.trim() !== '';
  const org = merged('organization');
  const missing = [
    !(filled(org.legalName) && filled(org.address) && Number(org.locationCount) > 0 && filled(org.organizationType)) && 'organization',
    ...PEOPLE.map((p) => !(filled(merged(p.key).name) && filled(merged(p.key).email)) && p.key),
  ].filter(Boolean) as string[];
  const alreadyReturned = !!read.data.submittedAt;

  const textField = (section: Section, field: string, label: string, max: number, type = 'text') => {
    const id = `ie-${section}-${field}`;
    return (
      <div>
        <label className={form.label} htmlFor={id}>{label}</label>
        <input id={id} type={type} className={form.input} maxLength={max}
          value={text(section, field)} onChange={(e) => set(section, field, e.target.value)} />
        <StaffMark field={staff[`${section}.${field}`]} />
      </div>
    );
  };

  return (
    <GroupCard id="ie-title" title="Edit their answers">
      <p className={page.itemMeta} style={{ maxWidth: '72ch' }}>
        Enter what they told you, or correct what they sent. Each answer you change is marked with
        how you got it. Their link keeps working, and if they change an answer on their own form
        afterwards, theirs replaces yours.
      </p>

      <h4 className={form.group}>How did you get these answers?</h4>
      <div className={form.row}>
        <div>
          <label className={form.label} htmlFor="ie-method">Source</label>
          <select id="ie-method" className={form.select} value={method} onChange={(e) => setMethod(e.target.value)}>
            <option value="">Choose one</option>
            {ENTRY_METHODS.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
          </select>
        </div>
        <div>
          <label className={form.label} htmlFor="ie-detail">
            {method === 'other' ? 'Where they came from' : <>Note <span className={form.optional}>optional</span></>}
          </label>
          <input id="ie-detail" className={form.input} maxLength={200} value={detail}
            aria-invalid={otherNeedsWords || undefined}
            onChange={(e) => setDetail(e.target.value)} />
          {otherNeedsWords && <p className={form.error}>Say where the answers came from.</p>}
        </div>
      </div>
      <div className={form.row} style={{ marginTop: 'var(--sp-3)' }}>
        <div>
          <label className={form.label} htmlFor="ie-given-name">Who gave them to you <span className={form.optional}>optional</span></label>
          <input id="ie-given-name" className={form.input} maxLength={PERSON_NAME_MAX} value={givenByName}
            onChange={(e) => setGivenByName(e.target.value)} />
        </div>
        <div>
          <label className={form.label} htmlFor="ie-given-email">Their email <span className={form.optional}>optional</span></label>
          <input id="ie-given-email" type="email" className={form.input} maxLength={EMAIL_MAX} value={givenByEmail}
            onChange={(e) => setGivenByEmail(e.target.value)} />
        </div>
      </div>

      <h4 className={form.group}>Their organization</h4>
      <div className={form.row}>
        {textField('organization', 'legalName', 'Full legal name', ORG_NAME_MAX)}
        {textField('organization', 'address', 'Business address', 500)}
      </div>
      <div className={form.row} style={{ marginTop: 'var(--sp-3)' }}>
        <div>
          <label className={form.label} htmlFor="ie-organization-locationCount">How many locations</label>
          <input id="ie-organization-locationCount" className={form.input} inputMode="numeric" maxLength={3}
            value={text('organization', 'locationCount')}
            onChange={(e) => {
              const digits = e.target.value.replace(/[^\d]/g, '').slice(0, 3);
              set('organization', 'locationCount', digits ? Number(digits) : null);
            }} />
          <StaffMark field={staff['organization.locationCount']} />
        </div>
        <div>
          <label className={form.label} htmlFor="ie-organization-organizationType">What kind of organization</label>
          <select id="ie-organization-organizationType" className={form.select} value={text('organization', 'organizationType')}
            onChange={(e) => set('organization', 'organizationType', e.target.value || null)}>
            <option value="">Not given</option>
            {ORGANIZATION_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
          </select>
          <StaffMark field={staff['organization.organizationType']} />
        </div>
        {textField('organization', 'targetGoLiveDate', 'Target go-live date', 10, 'date')}
      </div>

      {PEOPLE.map((p) => {
        const k = known[p.key];
        const empty = !text(p.key, 'name') && !text(p.key, 'email');
        return (
        <div key={p.key}>
          <h4 className={form.group}>{p.title}</h4>
          {k && empty && (
            <p className={page.itemMeta} style={{ marginTop: 0 }}>
              <button type="button" className={page.linkBtn} onClick={() => {
                set(p.key, 'name', k.name); set(p.key, 'email', k.email);
                if (k.title) set(p.key, 'title', k.title);
                if (k.phone) set(p.key, 'phone', k.phone);
              }}>
                Copy {k.name} from {k.from}
              </button>
            </p>
          )}
          <div className={form.row} style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}>
            {textField(p.key, 'name', 'Name', PERSON_NAME_MAX)}
            {textField(p.key, 'email', 'Email', EMAIL_MAX, 'email')}
            {textField(p.key, 'title', 'Job title', JOB_TITLE_MAX)}
            {textField(p.key, 'phone', 'Phone', PHONE_MAX, 'tel')}
          </div>
        </div>
        );
      })}

      <h4 className={form.group}>Anything their process requires</h4>
      <div className={form.row}>
        {textField('requirements', 'poNumber', 'Purchase order number', 100)}
        {textField('requirements', 'vendorPortal', 'Supplier portal to register in', 500)}
      </div>
      {(['needsBaa', 'needsSecurityReview'] as const).map((field) => (
        <div key={field}>
          <label className={form.check}>
            <input type="checkbox" checked={value('requirements', field) === true}
              onChange={(e) => set('requirements', field, e.target.checked)} />
            <span>{field === 'needsBaa' ? 'They need a business associate agreement' : 'Their IT or security team will want to review us'}</span>
          </label>
          <StaffMark field={staff[`requirements.${field}`]} />
        </div>
      ))}
      <div style={{ marginTop: 'var(--sp-3)' }}>
        <label className={form.label} htmlFor="ie-requirements-notes">Anything else</label>
        <textarea id="ie-requirements-notes" className={form.textarea} maxLength={2000}
          value={text('requirements', 'notes')} onChange={(e) => set('requirements', 'notes', e.target.value)} />
        <StaffMark field={staff['requirements.notes']} />
      </div>

      {!alreadyReturned && (
        <>
          <label className={form.check} style={{ marginTop: 'var(--sp-4)' }}>
            <input type="checkbox" checked={markReturned} disabled={missing.length > 0}
              onChange={(e) => setMarkReturned(e.target.checked)} />
            <span>
              Also mark the form as returned
              <span className={form.hint}>
                {missing.length > 0
                  ? `Not yet: still missing ${missing.map((m) => SECTION_WORDS[m] ?? m).join(', ')}.`
                  : 'As if they had submitted it themselves. The record says you returned it, and how.'}
              </span>
            </span>
          </label>
        </>
      )}

      {save.error && (
        <div className={`${page.notice} ${page.noticeDanger}`} role="alert" style={{ marginTop: 'var(--sp-3)' }}>
          {isCallFailure(save.error) ? save.error.message : 'The answers were not saved.'}
        </div>
      )}
      <div className={page.actions} style={{ marginTop: 'var(--sp-4)' }}>
        <button type="button" className={`${page.btn} ${page.btnPrimary}`} disabled={!ready} onClick={() => save.mutate()}>
          {save.isPending ? 'Saving…' : 'Save answers'}
        </button>
        <button type="button" className={page.btn} onClick={onDone}>Cancel</button>
        {method === '' && (changedCount > 0 || markReturned) && (
          <span className={page.itemMeta}>Choose how you got these answers first.</span>
        )}
      </div>
    </GroupCard>
  );
}
